<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { AUTOMATION_PRESETS } from './automationPresets';

const props = defineProps({
  initialPreset: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['usePreset']);
const { t } = useI18n();

const selectedPreset = ref(props.initialPreset);

const isDetail = computed(() => selectedPreset.value !== null);

function selectPreset(preset) {
  selectedPreset.value = preset;
}

function back() {
  selectedPreset.value = null;
}

function confirm() {
  emit('usePreset', selectedPreset.value.rule);
  selectedPreset.value = null;
}

// Map action_name → readable label
const ACTION_LABELS = {
  send_message: 'Send Message',
  send_attachment: 'Send Attachment',
  assign_agent: 'Assign Agent',
  assign_team: 'Assign Team',
  add_label: 'Add Label',
  remove_label: 'Remove Label',
  resolve_conversation: 'Resolve Conversation',
  open_conversation: 'Open Conversation',
  mute_conversation: 'Mute Conversation',
  snooze_conversation: 'Snooze Conversation',
  send_email_to_team: 'Send Email to Team',
  send_email_transcript: 'Send Email Transcript',
  send_webhook_event: 'Send Webhook Event',
  add_private_note: 'Add Private Note',
};

// Map attribute_key → readable label
const CONDITION_LABELS = {
  status: 'Status',
  content: 'Message Content',
  email: 'Email',
  inbox_id: 'Inbox',
  assignee_id: 'Assignee',
  team_id: 'Team',
  priority: 'Priority',
  last_activity_at: 'Last Activity (minutes)',
  conversation_language: 'Language',
  browser_language: 'Browser Language',
  country_code: 'Country',
  mail_subject: 'Email Subject',
  phone_number: 'Phone Number',
  referer: 'Referrer URL',
};

// Map filter_operator → readable symbol
const OPERATOR_LABELS = {
  equal_to: 'is',
  not_equal_to: 'is not',
  contains: 'contains',
  does_not_contain: 'does not contain',
  greater_than: '>',
  less_than: '<',
  starts_with: 'starts with',
  ends_with: 'ends with',
  present: 'is present',
  blank: 'is blank',
};

function formatCondition(c) {
  const key = CONDITION_LABELS[c.attribute_key] || c.attribute_key;
  const op = OPERATOR_LABELS[c.filter_operator] || c.filter_operator;
  const vals = Array.isArray(c.values) ? c.values.join(', ') : c.values;
  return vals ? `${key} ${op} "${vals}"` : `${key} ${op}`;
}

function formatAction(a) {
  const label = ACTION_LABELS[a.action_name] || a.action_name;
  const params = a.action_params?.length ? a.action_params.join(', ') : '';
  return params ? `${label}: ${params}` : label;
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-6 py-5 border-b border-n-weak shrink-0"
    >
      <button
        v-if="isDetail"
        type="button"
        class="flex items-center justify-center rounded-lg size-7 text-n-slate-9 hover:bg-n-alpha-2"
        @click="back"
      >
        <span class="i-lucide-arrow-left size-4" />
      </button>
      <div class="flex-1 min-w-0">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{
            isDetail
              ? `${selectedPreset.icon} ${selectedPreset.name}`
              : t('AUTOMATION.PRESET.MODAL_TITLE')
          }}
        </h2>
        <p class="mt-0.5 text-sm text-n-slate-9">
          {{
            isDetail
              ? selectedPreset.description
              : t('AUTOMATION.PRESET.MODAL_DESC')
          }}
        </p>
      </div>
    </div>

    <!-- Step 1: Preset grid -->
    <div v-if="!isDetail" class="p-6 overflow-y-auto">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          v-for="preset in AUTOMATION_PRESETS"
          :key="preset.id"
          type="button"
          class="flex flex-col items-start w-full gap-2 p-4 text-left transition-colors border rounded-xl border-n-weak bg-n-alpha-1 hover:border-woot-500 hover:bg-woot-25 group"
          @click="selectPreset(preset)"
        >
          <div class="flex items-center justify-between w-full">
            <span class="text-xl leading-none">{{ preset.icon }}</span>
            <span
              class="invisible text-xs font-medium text-woot-600 group-hover:visible"
            >
              {{ t('AUTOMATION.PRESET.PREVIEW') }}
            </span>
          </div>
          <div>
            <p class="text-sm font-semibold text-n-slate-12">
              {{ preset.name }}
            </p>
            <p class="mt-0.5 text-xs leading-relaxed text-n-slate-9">
              {{ preset.description }}
            </p>
          </div>
          <div class="flex flex-wrap gap-1.5 mt-auto pt-1">
            <span
              class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-woot-100 text-woot-700"
            >
              {{ preset.eventLabel }}
            </span>
            <span
              class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700"
            >
              {{ preset.actionLabel }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <!-- Step 2: Detail / preview — mirrors Add Automation Rule visual flow -->
    <div v-else class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 px-6 py-5 overflow-y-auto">
        <!-- TRIGGER block -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div
            class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="i-lucide-zap size-3.5 text-n-slate-9 shrink-0" />
            <div>
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
              >
                {{ t('AUTOMATION.FORM.TRIGGER_LABEL') }}
              </p>
              <p class="text-xs text-n-slate-8 mt-0.5">
                {{ t('AUTOMATION.FORM.TRIGGER_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="px-4 py-3">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-lg bg-woot-100 text-woot-700"
            >
              <span class="i-lucide-zap size-3.5" />
              {{ selectedPreset.eventLabel }}
            </span>
          </div>
        </div>

        <!-- Connector -->
        <div class="flex flex-col items-center py-1">
          <div class="w-px h-4 border-l border-dashed border-n-slate-5" />
          <span class="i-lucide-chevron-down size-3.5 text-n-slate-6 -mt-1" />
        </div>

        <!-- CONDITIONS block -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div
            class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="i-lucide-filter size-3.5 text-n-slate-9 shrink-0" />
            <div>
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
              >
                {{ t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-8 mt-0.5">
                {{ t('AUTOMATION.FORM.CONDITIONS_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <ul class="divide-y divide-n-weak">
            <li
              v-for="(cond, i) in selectedPreset.rule.conditions"
              :key="i"
              class="flex items-center gap-2 px-4 py-3 text-sm text-n-slate-11"
            >
              <span
                class="i-lucide-dot-square size-3.5 shrink-0 text-n-slate-7"
              />
              {{ formatCondition(cond) }}
            </li>
          </ul>
        </div>

        <!-- Connector -->
        <div class="flex flex-col items-center py-1">
          <div class="w-px h-4 border-l border-dashed border-n-slate-5" />
          <span class="i-lucide-chevron-down size-3.5 text-n-slate-6 -mt-1" />
        </div>

        <!-- ACTIONS block -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
        >
          <div
            class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <span class="i-lucide-zap size-3.5 text-n-slate-9 shrink-0" />
            <div>
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
              >
                {{ t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-8 mt-0.5">
                {{ t('AUTOMATION.FORM.ACTIONS_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <ul class="divide-y divide-n-weak">
            <li
              v-for="(action, i) in selectedPreset.rule.actions"
              :key="i"
              class="flex items-center gap-2 px-4 py-3 text-sm text-n-slate-11"
            >
              <span class="i-lucide-play size-3.5 shrink-0 text-green-500" />
              {{ formatAction(action) }}
            </li>
          </ul>
        </div>

        <!-- END OF FLOW -->
        <div class="flex flex-col items-center pt-1 pb-3">
          <div class="w-px h-4 border-l border-dashed border-n-slate-5" />
          <span
            class="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-n-slate-9 border border-n-weak rounded-full bg-n-solid-1"
          >
            <span class="size-1.5 rounded-full bg-n-slate-5" />
            {{ t('AUTOMATION.FORM.END_OF_FLOW') }}
          </span>
        </div>

        <!-- Note -->
        <p class="text-xs text-n-slate-8 text-center">
          {{ t('AUTOMATION.PRESET.DETAIL_NOTE') }}
        </p>
      </div>

      <!-- Footer actions -->
      <div
        class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak shrink-0"
      >
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition-colors rounded-lg text-n-slate-11 hover:bg-n-alpha-2"
          @click="back"
        >
          {{ t('AUTOMATION.PRESET.BACK') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white transition-colors rounded-lg bg-woot-500 hover:bg-woot-600"
          @click="confirm"
        >
          {{ t('AUTOMATION.PRESET.CREATE') }}
        </button>
      </div>
    </div>
  </div>
</template>
